import axios from "axios";
import VERSE_BUNDLE from "@/data/verses.json";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : null;

// Short timeout so the app feels fast on flaky networks / inside the iOS shell.
const BACKEND_TIMEOUT_MS = 2500;

// ---------- Local-date helper ----------
export function localDateISO(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// ---------- Local-data implementations (offline fallback) ----------
const LOCAL_VERSES = VERSE_BUNDLE.verses;

function localCategories() {
  return {
    categories: VERSE_BUNDLE.categories,
    counts: VERSE_BUNDLE.counts,
    total_verses: VERSE_BUNDLE.total_verses,
  };
}

function localRandom({ category, excludeReference } = {}) {
  let pool = LOCAL_VERSES;
  if (category && category !== "all") {
    pool = LOCAL_VERSES.filter((v) => v.category === category);
    if (pool.length === 0) pool = LOCAL_VERSES;
  }
  if (excludeReference && pool.length > 1) {
    const filtered = pool.filter((v) => v.reference !== excludeReference);
    if (filtered.length) pool = filtered;
  }
  return pool[Math.floor(Math.random() * pool.length)];
}

// SHA-256 → first 8 hex chars → mod len(verses). Identical to backend logic
// so the daily verse stays in sync with the server when both are available.
async function sha256Hex(input) {
  const buf = new TextEncoder().encode(input);
  const hashBuf = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function localDaily(date) {
  const iso = date || localDateISO();
  // Use first 13 hex chars → fits safely in a JS number (52-bit mantissa).
  const hex = (await sha256Hex(iso)).slice(0, 13);
  const idx = parseInt(hex, 16) % LOCAL_VERSES.length;
  return LOCAL_VERSES[idx];
}

// ---------- Hybrid request wrapper ----------
// Try the backend (with timeout). On any failure, transparently fall back
// to the bundled offline data. This keeps the app working in airplane mode,
// in App Store review, and if the API is ever down.
async function tryBackend(path, params) {
  if (!API) return null;
  try {
    const { data } = await axios.get(`${API}${path}`, {
      params,
      timeout: BACKEND_TIMEOUT_MS,
    });
    return data;
  } catch {
    return null;
  }
}

// ---------- Public API (unchanged signatures) ----------
export async function fetchCategories() {
  const remote = await tryBackend("/verses/categories");
  return remote || localCategories();
}

export async function fetchRandomVerse({ category, excludeReference } = {}) {
  const params = {};
  if (category && category !== "all") params.category = category;
  if (excludeReference) params.exclude_reference = excludeReference;
  const remote = await tryBackend("/verses/random", params);
  return remote || localRandom({ category, excludeReference });
}

export async function fetchDailyVerse(date) {
  const iso = date || localDateISO();
  const remote = await tryBackend("/verses/daily", { date: iso });
  return remote || (await localDaily(iso));
}
